import { action, computed, observable } from 'mobx';
import {
  CurrencyCurrency,
  type Site as SiteSchema,
  type SiteApi
} from '@linktivity/ars-neo';
import type { LanguageCodeType } from '@link/share/services/types';
import { setGa4SiteAgentId } from '../vendors/ga4/site';

const storage = window.sessionStorage;

/**
 * Schema loading, currency and the getters every app's Site store shares. Apps
 * add their `AppCapabilities` and any getters of their own.
 */
export default abstract class Site {
  @observable accessor siteSchema: SiteSchema = {};
  @observable accessor siteLoading: boolean = false;
  // Flips to true once the latest schema fetch settles (success or failure);
  // the profile gates use it to tell "fetch failed" from "not fetched yet".
  @observable accessor siteSettled: boolean = false;
  @observable accessor _currency: CurrencyCurrency | null =
    (storage.getItem('currency') as CurrencyCurrency) || null;

  // Bumped per fetch so an older response landing late (a quick language
  // switch) neither overwrites the newer schema nor clears the loading flag.
  private siteRequestId = 0;

  protected abstract get siteApi(): SiteApi;

  async getSiteSchema(languageCode: LanguageCodeType) {
    const requestId = ++this.siteRequestId;
    const isLatest = () => requestId === this.siteRequestId;
    this.setSiteLoading(true);
    try {
      const res = await this.siteApi.getSiteSchema({ languageCode });
      if (isLatest()) this.setSiteSchema(res?.site);
      return res?.site ?? {};
    } finally {
      if (isLatest()) {
        this.setSiteLoading(false);
        this.setSiteSettled(true);
      }
    }
  }

  @action
  setSiteLoading(siteLoading: boolean) {
    this.siteLoading = siteLoading;
  }

  @action
  setSiteSettled(siteSettled: boolean) {
    this.siteSettled = siteSettled;
  }

  // A response without `site` keeps the schema already loaded.
  @action
  setSiteSchema(siteSchema?: SiteSchema) {
    if (siteSchema) {
      this.siteSchema = siteSchema;
      setGa4SiteAgentId(siteSchema.agentId);
    }
  }

  getSite() {
    return this.siteSchema;
  }

  get currency() {
    return (
      this._currency ||
      this.siteSchema?.siteLayout?.currency ||
      CurrencyCurrency.Jpy
    );
  }

  @action
  setCurrency(currency: CurrencyCurrency) {
    this._currency = currency;
    storage.setItem('currency', currency);
  }

  @computed
  get supportedLanguageCodes() {
    return (
      this.siteSchema?.siteLayout?.headerConfig?.supportedLanguageCodes || []
    );
  }

  @computed
  get supportedCurrencies() {
    return this.siteSchema?.siteLayout?.headerConfig?.supportedCurrencies || [];
  }

  @computed
  get showOtherCurrencies() {
    return (
      this.siteSchema?.siteLayout?.headerConfig?.showOtherCurrencies ?? false
    );
  }

  @computed
  get userProfileForm() {
    return this.siteSchema?.siteLayout?.authConfig?.userProfileForm;
  }
}
